import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Lock, ArrowLeft, Play, Pause, ChevronLeft, ChevronRight, Printer, FileText } from 'lucide-react';

// Presenter Notes Content for Each Slide
const presenterNotes = {
  0: { // Slide 1 - Enforcement and Licensing Opportunity
    title: "Enforcement and Licensing Opportunity",
    content: `We are a private, R&D-driven company that develops advanced data formalization, compression, and content/data protection technologies. Over that arc we built Project Goliath: Intelligent Data Density Optimization (IDDO), powered by patented worldwide MSML principles (value-guided, multi-level formalization) and an applied extension we refer to as MSML-LLMC.

Today we'll discuss our MSML portfolio and a potential partnership aimed at licensing of AI efficiency economics methods at scale.

**Agenda overview:**
• We'll briefly review the patent family
• Explain the core technology and its extension to model compression (MSML-LLMC)
• Outline enforcement wedges and evidence plans
• Present a shortlist of high-value targets
• Discuss damages logic and royalty models
• Explore why Acacia is the right partner`
  },
  1: { // Slide 2 - AI economics are breaking
    title: "AI Economics & Efficiency",
    content: `The AI market is converging on efficiency as the binding constraint: less compute, less energy, less bandwidth, while pushing toward human-brain-like throughput.

These are precisely the tasks MSML was designed to address and formalize where proof points are increasingly and vividly observable in currently shipped products and developer stacks.`
  },
  2: { // Slide 3 - Portfolio-grade claims
    title: "Background & Patent Family",
    content: `**Family overview:** MSML stems from a 2011 priority date and is protected by patents in the US, Canada, Europe and multiple Asian jurisdictions. The core US patent (US 10140305 B2) and its continuations cover methods and apparatuses for structuring information via successive manipulation processes and hierarchical object clusters. European, Canadian, Hong Kong, China, Russia and India counterparts are granted and active until 2032. Additional applications strengthen the portfolio for audio codecs, media containers and steganography.

These patent portfolio claims survive eligibility and obviousness challenges because they improve data storage and retrieval using specific algorithms and metadata.

**Why it survives §101 (eligibility):**
The claim recites a series of concrete data-processing operations (analyzing a file, applying transformation processes, forming structured data with representative elements and interrelationships). These steps require specific manipulation processes (phaseless coding, structured analysis, entropy compression, affine transforms, etc.) and produce a technological result: a hierarchical data structure enabling efficient storage and retrieval. This goes beyond an abstract idea; it's akin to "improved data-storage techniques" which have been held eligible. The claim also recites a threshold preserving information value, tying the processes to physical limitations (human perceptual thresholds), further grounding it in technology.

**Why it survives obviousness (§103):**
The combination of multi-level hierarchy, representative elements with interrelationship indicia, and successive manipulation until no further reduction was not known in prior art. Traditional compression techniques do not create a general hierarchical information file across arbitrary data types, nor do they explicitly track relationships and definitions of removed elements. The claim's mixture of removal and representation with definitional metadata results in a self-describing structured file, a non-obvious improvement over existing compression algorithms.`
  },
  3: { // Slide 4 - Three disciplined wedges
    title: "Enforcement Wedges",
    content: `**Key talking points for each wedge:**

**Wedge 1 - AI Data Pipelines & RAG:**
Focus on how modern AI systems depend on structured data ingestion and retrieval. Evidence is abundant in public documentation and SDKs.

**Wedge 2 - Inference Optimization:**
Highlight the specific techniques (pruning, quantization, mixed precision) that map directly to our patent claims around component valuation and hierarchical structuring.

**Wedge 3 - Media Provenance & Security:**
Emphasize the growing regulatory pressure for content authenticity and how our watermarking and traceability claims position us well.`
  },
  4: { // Slide 5 - Disciplined enforcement
    title: "Execution Strategy",
    content: `**Timeline walkthrough:**
• Phase 1: Claim chart development (2-4 weeks intensive work)
• Phase 2: Evidence collection from public sources
• Phase 3: Initial outreach to top-tier targets
• Phase 4: Escalation only if licensing discussions stall

**Evidence stack priority:**
Start with public documentation as it's defensible and comprehensive. SDK analysis provides technical depth. Expert reverse engineering reserved for contested claims.`
  },
  5: { // Slide 6 - Economics & Structure
    title: "Deal Structure Notes",
    content: `**Key negotiation points:**

**Up-front payment:** The partnership agreement includes an upfront fee, which serves to recognize the prior investment and support the continuation of R&D efforts

**Revenue split rationale:** 50/50 is standard when both parties contribute significant value. We bring the IP; partner brings enforcement infrastructure and capital.

**Royalty rate benchmarks:**
• 0.1-0.2% for AI-API is conservative given patent strength
• 1-2% for enterprise software aligns with industry norms
• Per-unit hardware fees reflect embedded IP value

**5-year projection assumptions:**
Based on licensing ~10 major targets at modest rates. Significant upside exists if more licensees added or rates increased through successful enforcement.`
  }
};

// Presenter Notes Modal Component
const PresenterNotesModal = ({ isOpen, onClose, slideIndex }) => {
  const note = presenterNotes[slideIndex];
  
  if (!isOpen || !note) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[10000] flex items-center justify-end pointer-events-none"
      >
        <motion.div
          initial={{ opacity: 0, x: 50 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: 50 }}
          transition={{ type: "spring", damping: 25, stiffness: 300 }}
          className="pointer-events-auto mr-4 my-4 w-[420px] max-h-[calc(100vh-120px)] bg-slate-900/95 border border-cyan-400/40 rounded-xl shadow-2xl shadow-cyan-500/10 backdrop-blur-md overflow-hidden"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="sticky top-0 bg-gradient-to-r from-slate-900 via-slate-800 to-slate-900 border-b border-cyan-400/30 px-5 py-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-lg bg-cyan-500/20 border border-cyan-400/40 flex items-center justify-center">
                <FileText size={16} className="text-cyan-400" />
              </div>
              <div>
                <h3 className="text-white font-semibold text-sm">Presenter Notes</h3>
                <p className="text-cyan-400/80 text-xs">Slide {slideIndex + 1} of 6</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="w-8 h-8 rounded-lg bg-slate-800 border border-slate-700 hover:border-cyan-400/50 flex items-center justify-center transition-colors group"
            >
              <X size={16} className="text-gray-400 group-hover:text-cyan-400" />
            </button>
          </div>

          {/* Title */}
          <div className="px-5 py-3 bg-slate-800/50 border-b border-slate-700/50">
            <h4 className="text-cyan-300 font-medium text-base">{note.title}</h4>
          </div>

          {/* Content */}
          <div className="px-5 py-4 overflow-y-auto max-h-[calc(100vh-280px)] custom-scrollbar">
            <div className="text-gray-300 text-sm leading-relaxed whitespace-pre-wrap prose prose-invert prose-sm max-w-none">
              {note.content.split('\n').map((line, idx) => {
                // Handle bold text marked with **
                if (line.includes('**')) {
                  const parts = line.split(/\*\*(.*?)\*\*/g);
                  return (
                    <p key={idx} className="mb-3">
                      {parts.map((part, partIdx) => 
                        partIdx % 2 === 1 
                          ? <span key={partIdx} className="text-cyan-300 font-semibold">{part}</span>
                          : part
                      )}
                    </p>
                  );
                }
                // Handle bullet points
                if (line.trim().startsWith('•')) {
                  return (
                    <p key={idx} className="mb-2 pl-4 text-gray-400">
                      <span className="text-cyan-400 mr-2">•</span>
                      {line.trim().substring(1).trim()}
                    </p>
                  );
                }
                // Regular paragraph
                if (line.trim()) {
                  return <p key={idx} className="mb-3">{line}</p>;
                }
                return <div key={idx} className="h-2" />;
              })}
            </div>
          </div>

          {/* Footer */}
          <div className="sticky bottom-0 bg-slate-900/95 border-t border-slate-700/50 px-5 py-3">
            <div className="flex items-center justify-between text-xs text-gray-500">
              <span>Press <kbd className="px-1.5 py-0.5 bg-slate-800 rounded border border-slate-700 text-gray-400 font-mono">N</kbd> to toggle notes</span>
              <span className="text-cyan-400/60">General Harmonics</span>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

// Password Gate Modal Component
const PasswordGateModal = ({ isOpen, onClose, onSuccess }) => {
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');

  const handleSubmit = (e) => {
    e.preventDefault();
    if (password.toLowerCase() === 'arg deck') {
      onSuccess();
      setError('');
    } else {
      setError('Access denied.');
      setPassword('');
    }
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm"
        onClick={onClose}
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.95 }}
          onClick={(e) => e.stopPropagation()}
          className="bg-slate-900 border border-cyan-400/30 rounded-lg p-8 max-w-md w-full mx-4"
        >
          <div className="flex justify-between items-start mb-6">
            <div>
              <h3 className="text-2xl font-bold text-white mb-2">Restricted Access</h3>
              <p className="text-gray-400 text-sm">
                This material is intended for qualified licensing and enforcement partners.
              </p>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-white transition-colors"
            >
              <X size={24} />
            </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter access code"
                className="w-full bg-slate-800 border border-cyan-400/30 rounded px-4 py-3 text-white placeholder-gray-500 focus:outline-none focus:border-cyan-400 transition-colors"
                autoFocus
              />
              {error && (
                <p className="text-red-400 text-sm mt-2">{error}</p>
              )}
            </div>

            <button
              type="submit"
              className="w-full bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-600 hover:to-blue-700 text-white font-semibold py-3 px-6 rounded transition-all"
            >
              Enter
            </button>
          </form>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

// Individual Slide Components
const Slide1 = ({ isPitchMode, visibleBullets }) => (
  <div className="relative h-screen w-full flex items-center justify-center overflow-hidden">
    {/* Background Video */}
    <video
      autoPlay
      loop
      muted
      playsInline
      className="absolute inset-0 w-full h-full object-cover opacity-30"
      poster="data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='1920' height='1080'%3E%3Cdefs%3E%3ClinearGradient id='g' x1='0%25' y1='0%25' x2='100%25' y2='100%25'%3E%3Cstop offset='0%25' style='stop-color:%23134e4a;stop-opacity:1' /%3E%3Cstop offset='100%25' style='stop-color:%230f172a;stop-opacity:1' /%3E%3C/linearGradient%3E%3C/defs%3E%3Crect fill='url(%23g)' width='1920' height='1080'/%3E%3C/svg%3E"
    >
      <source src="https://assets.mixkit.co/videos/preview/mixkit-abstract-blue-background-with-floating-particles-4168-large.mp4" type="video/mp4" />
    </video>

    {/* Content */}
    <div className="relative z-10 text-center px-8 max-w-4xl">
      <motion.h1
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8 }}
        className="text-6xl font-bold text-white mb-6 tracking-tight"
      >
        Enforcement and Licensing Opportunity
      </motion.h1>

      <motion.p
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, delay: 0.2 }}
        className="text-2xl text-gray-300 mb-12"
      >
        A portfolio-grade AI infrastructure licensing program
      </motion.p>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, delay: 0.4 }}
        className="flex justify-center gap-4 mb-16"
      >
        <span className="px-4 py-2 bg-cyan-500/20 border border-cyan-400/40 rounded text-cyan-300 text-sm">
          MSML™ Patent Family
        </span>
        <span className="px-4 py-2 bg-cyan-500/20 border border-cyan-400/40 rounded text-cyan-300 text-sm">
          World-wide Coverage
        </span>
        <span className="px-4 py-2 bg-cyan-500/20 border border-cyan-400/40 rounded text-cyan-300 text-sm">
          Licensing-first posture
        </span>
      </motion.div>

      <motion.p
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.8, delay: 0.6 }}
        className="text-sm text-gray-500"
      >
        General Harmonics – Confidential
      </motion.p>
    </div>
  </div>
);

const Slide2 = ({ isPitchMode, visibleBullets }) => (
  <div className="relative h-screen w-full flex items-center justify-center overflow-hidden">
    <video
      autoPlay
      loop
      muted
      playsInline
      className="absolute inset-0 w-full h-full object-cover opacity-20"
      poster="data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='1920' height='1080'%3E%3Cdefs%3E%3ClinearGradient id='g' x1='0%25' y1='0%25' x2='100%25' y2='100%25'%3E%3Cstop offset='0%25' style='stop-color:%23134e4a;stop-opacity:1' /%3E%3Cstop offset='100%25' style='stop-color:%230f172a;stop-opacity:1' /%3E%3C/linearGradient%3E%3C/defs%3E%3Crect fill='url(%23g)' width='1920' height='1080'/%3E%3C/svg%3E"
    >
      <source src="https://assets.mixkit.co/videos/preview/mixkit-data-center-servers-closeup-50903-large.mp4" type="video/mp4" />
    </video>

    <div className="relative z-10 px-8 max-w-5xl">
      <motion.h2
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="text-5xl font-bold text-white mb-12"
      >
        AI economics are breaking. Efficiency is the new competitive advantage.
      </motion.h2>

      <div className="grid md:grid-cols-2 gap-8 mb-12">
        <div className="space-y-6">
          {(!isPitchMode || visibleBullets >= 1) && (
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              className="flex items-start gap-4"
            >
              <div className="w-2 h-2 rounded-full bg-cyan-400 mt-2" />
              <p className="text-gray-300 text-lg">
                Compute, energy, and bandwidth now constrain AI growth
              </p>
            </motion.div>
          )}

          {(!isPitchMode || visibleBullets >= 2) && (
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.1 }}
              className="flex items-start gap-4"
            >
              <div className="w-2 h-2 rounded-full bg-cyan-400 mt-2" />
              <p className="text-gray-300 text-lg">
                Modern AI stacks depend on structured data and optimized inference
              </p>
            </motion.div>
          )}

          {(!isPitchMode || visibleBullets >= 3) && (
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.2 }}
              className="flex items-start gap-4"
            >
              <div className="w-2 h-2 rounded-full bg-cyan-400 mt-2" />
              <p className="text-gray-300 text-lg">
                This opportunity addresses the substrate, not the model
              </p>
            </motion.div>
          )}
        </div>

        {(!isPitchMode || visibleBullets >= 4) && (
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.3 }}
            className="bg-slate-900/80 border border-cyan-400/30 rounded-lg p-6"
          >
            <h3 className="text-cyan-400 font-semibold mb-4 text-lg">Key Proof Callout</h3>
            <div className="space-y-3 text-gray-300">
              <p>• 4–8× smaller LLM weights (≤0.5pp accuracy change)</p>
              <p>• 2–3× lower energy per token</p>
              <p>• 10–30× less long-context traffic (KV-cache compression)</p>
            </div>
          </motion.div>
        )}
      </div>
    </div>
  </div>
);

const Slide3 = ({ isPitchMode, visibleBullets }) => (
  <div className="relative h-screen w-full flex items-center justify-center overflow-hidden">
    <video
      autoPlay
      loop
      muted
      playsInline
      className="absolute inset-0 w-full h-full object-cover opacity-20"
      poster="data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='1920' height='1080'%3E%3Cdefs%3E%3ClinearGradient id='g' x1='0%25' y1='0%25' x2='100%25' y2='100%25'%3E%3Cstop offset='0%25' style='stop-color:%23134e4a;stop-opacity:1' /%3E%3Cstop offset='100%25' style='stop-color:%230f172a;stop-opacity:1' /%3E%3C/linearGradient%3E%3C/defs%3E%3Crect fill='url(%23g)' width='1920' height='1080'/%3E%3C/svg%3E"
    >
      <source src="https://assets.mixkit.co/videos/preview/mixkit-circuit-board-close-up-1058-large.mp4" type="video/mp4" />
    </video>

    <div className="relative z-10 px-8 max-w-6xl">
      <motion.h2
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="text-5xl font-bold text-white mb-12"
      >
        Portfolio-grade claims with concrete technical grounding
      </motion.h2>

      <div className="grid md:grid-cols-2 gap-8">
        {(!isPitchMode || visibleBullets >= 1) && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-slate-900/80 border border-cyan-400/30 rounded-lg p-8"
          >
            <h3 className="text-cyan-400 font-semibold mb-6 text-xl">Patent Family Snapshot</h3>
            <div className="space-y-4 text-gray-300">
              <p>• Granted core patents (US)</p>
              <p>• Continuations and fortified filings</p>
              <p>• Canadian, EU, Hong Kong, China, India, Russia counterparts</p>
              <p>• Priority timeline visualization</p>
              <p className="text-green-400">• Maintenance: Active</p>
            </div>
          </motion.div>
        )}

        {(!isPitchMode || visibleBullets >= 2) && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-slate-900/80 border border-cyan-400/30 rounded-lg p-8"
          >
            <h3 className="text-cyan-400 font-semibold mb-6 text-xl">Why Enforceable</h3>
            <div className="space-y-4 text-gray-300">
              <p>• System and method claims</p>
              <p>• Multi-level information formalization</p>
              <p>• Hierarchical grouping + controlled fidelity</p>
              <p>• Designed to survive §101 scrutiny</p>
            </div>
          </motion.div>
        )}
      </div>
    </div>
  </div>
);

const Slide4 = ({ isPitchMode, visibleBullets }) => (
  <div className="relative h-screen w-full flex items-center justify-center overflow-hidden">
    <video
      autoPlay
      loop
      muted
      playsInline
      className="absolute inset-0 w-full h-full object-cover opacity-20"
      poster="data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='1920' height='1080'%3E%3Cdefs%3E%3ClinearGradient id='g' x1='0%25' y1='0%25' x2='100%25' y2='100%25'%3E%3Cstop offset='0%25' style='stop-color:%23134e4a;stop-opacity:1' /%3E%3Cstop offset='100%25' style='stop-color:%230f172a;stop-opacity:1' /%3E%3C/linearGradient%3E%3C/defs%3E%3Crect fill='url(%23g)' width='1920' height='1080'/%3E%3C/svg%3E"
    >
      <source src="https://assets.mixkit.co/videos/preview/mixkit-network-connections-animation-4479-large.mp4" type="video/mp4" />
    </video>

    <div className="relative z-10 px-8 max-w-5xl">
      <motion.h2
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="text-5xl font-bold text-white mb-12"
      >
        Three disciplined wedges. Real evidence paths.
      </motion.h2>

      <div className="space-y-6">
        {(!isPitchMode || visibleBullets >= 1) && (
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            className="bg-slate-900/80 border border-cyan-400/30 rounded-lg p-6"
          >
            <h3 className="text-cyan-400 font-semibold mb-3 text-xl">Wedge 1: AI Data Pipelines & RAG</h3>
            <div className="grid md:grid-cols-3 gap-4 mb-3">
              <p className="text-gray-300">• Multi-format ingestion</p>
              <p className="text-gray-300">• Structured corpora</p>
              <p className="text-gray-300">• Indexed retrieval</p>
            </div>
            <p className="text-gray-500 text-sm">Evidence sources: docs, SDKs, runtime behavior</p>
          </motion.div>
        )}

        {(!isPitchMode || visibleBullets >= 2) && (
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.1 }}
            className="bg-slate-900/80 border border-cyan-400/30 rounded-lg p-6"
          >
            <h3 className="text-cyan-400 font-semibold mb-3 text-xl">Wedge 2: Inference Optimization</h3>
            <div className="grid md:grid-cols-3 gap-4 mb-3">
              <p className="text-gray-300">• Component valuation</p>
              <p className="text-gray-300">• Pruning / quantization</p>
              <p className="text-gray-300">• Mixed precision inference</p>
            </div>
            <p className="text-gray-500 text-sm">Evidence sources: docs, SDKs, runtime behavior</p>
          </motion.div>
        )}

        {(!isPitchMode || visibleBullets >= 3) && (
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-slate-900/80 border border-cyan-400/30 rounded-lg p-6"
          >
            <h3 className="text-cyan-400 font-semibold mb-3 text-xl">Wedge 3: Media Provenance & Security</h3>
            <div className="grid md:grid-cols-3 gap-4 mb-3">
              <p className="text-gray-300">• Structured assets</p>
              <p className="text-gray-300">• Watermarking</p>
              <p className="text-gray-300">• Traceability & compliance</p>
            </div>
            <p className="text-gray-500 text-sm">Evidence sources: docs, SDKs, runtime behavior</p>
          </motion.div>
        )}
      </div>
    </div>
  </div>
);

const Slide5 = ({ isPitchMode, visibleBullets }) => (
  <div className="relative h-screen w-full flex items-center justify-center overflow-hidden">
    <video
      autoPlay
      loop
      muted
      playsInline
      className="absolute inset-0 w-full h-full object-cover opacity-20"
      poster="data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='1920' height='1080'%3E%3Cdefs%3E%3ClinearGradient id='g' x1='0%25' y1='0%25' x2='100%25' y2='100%25'%3E%3Cstop offset='0%25' style='stop-color:%23134e4a;stop-opacity:1' /%3E%3Cstop offset='100%25' style='stop-color:%230f172a;stop-opacity:1' /%3E%3C/linearGradient%3E%3C/defs%3E%3Crect fill='url(%23g)' width='1920' height='1080'/%3E%3C/svg%3E"
    >
      <source src="https://assets.mixkit.co/videos/preview/mixkit-programming-code-on-a-screen-4576-large.mp4" type="video/mp4" />
    </video>

    <div className="relative z-10 px-8 max-w-6xl">
      <motion.h2
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="text-5xl font-bold text-white mb-12"
      >
        A disciplined enforcement and licensing program
      </motion.h2>

      <div className="grid md:grid-cols-2 gap-8">
        {(!isPitchMode || visibleBullets >= 1) && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-slate-900/80 border border-cyan-400/30 rounded-lg p-8"
          >
            <h3 className="text-cyan-400 font-semibold mb-6 text-xl">Timeline</h3>
            <div className="space-y-4">
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-full bg-cyan-500 flex items-center justify-center text-white font-bold text-sm">1</div>
                <p className="text-gray-300 pt-1">Claim chart sprint (weeks)</p>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-full bg-cyan-500 flex items-center justify-center text-white font-bold text-sm">2</div>
                <p className="text-gray-300 pt-1">Evidence collection</p>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-full bg-cyan-500 flex items-center justify-center text-white font-bold text-sm">3</div>
                <p className="text-gray-300 pt-1">Targeted outreach</p>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-full bg-cyan-500 flex items-center justify-center text-white font-bold text-sm">4</div>
                <p className="text-gray-300 pt-1">Escalation funnel (if needed)</p>
              </div>
            </div>
          </motion.div>
        )}

        {(!isPitchMode || visibleBullets >= 2) && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-slate-900/80 border border-cyan-400/30 rounded-lg p-8"
          >
            <h3 className="text-cyan-400 font-semibold mb-6 text-xl">Evidence Stack</h3>
            <div className="space-y-3 text-gray-300">
              <p>• Public documentation</p>
              <p>• SDK behavior</p>
              <p>• Configuration artifacts</p>
              <p>• Telemetry / logs (where accessible)</p>
              <p>• Expert reverse engineering</p>
            </div>
          </motion.div>
        )}
      </div>
    </div>
  </div>
);

const Slide6 = ({ isPitchMode, visibleBullets }) => (
  <div className="relative h-screen w-full flex items-center justify-center overflow-hidden">
    <video
      autoPlay
      loop
      muted
      playsInline
      className="absolute inset-0 w-full h-full object-cover opacity-20"
      poster="data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='1920' height='1080'%3E%3Cdefs%3E%3ClinearGradient id='g' x1='0%25' y1='0%25' x2='100%25' y2='100%25'%3E%3Cstop offset='0%25' style='stop-color:%23134e4a;stop-opacity:1' /%3E%3Cstop offset='100%25' style='stop-color:%230f172a;stop-opacity:1' /%3E%3C/linearGradient%3E%3C/defs%3E%3Crect fill='url(%23g)' width='1920' height='1080'/%3E%3C/svg%3E"
    >
      <source src="https://assets.mixkit.co/videos/preview/mixkit-abstract-financial-data-animation-4480-large.mp4" type="video/mp4" />
    </video>

    <div className="relative z-10 px-8 max-w-7xl w-full">
      <motion.h2
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="text-5xl font-bold text-white mb-8"
      >
        Economics & Structure
      </motion.h2>

      {/* Proposed Structure Section */}
      {(!isPitchMode || visibleBullets >= 1) && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-6"
        >
          <div className="bg-slate-900/80 border border-cyan-400/30 rounded-lg p-6">
            <h3 className="text-cyan-400 font-semibold mb-4 text-xl">Proposed Structure</h3>
            <div className="space-y-3 text-gray-300 text-sm">
              <p><strong className="text-cyan-300">Exclusive licensing and partnership:</strong> GH grants ARG an exclusive worldwide licence to the MSML/LLMC portfolio, with rights to sublicense and enforce. ARG takes responsibility for claim charting, evidence development and negotiations.</p>
              <p className="text-gray-400 italic">Agreement includes option for ARG to purchase portfolio outright after set term.</p>
            </div>
          </div>
        </motion.div>
      )}

      <div className="grid md:grid-cols-2 gap-6 mb-6">
        {/* Up-front and Running Royalties */}
        {(!isPitchMode || visibleBullets >= 2) && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="bg-slate-900/80 border border-cyan-400/30 rounded-lg p-6"
          >
            <h3 className="text-cyan-400 font-semibold mb-4 text-lg">Up-front & Running Royalties</h3>
            <div className="space-y-3 text-gray-300 text-sm">
              <div>
                <p className="font-semibold text-cyan-300 mb-1">Up-front payment</p>
                <p>An upfront fee is structured into the partnership to both acknowledge the value of the past investment and to fund ongoing R&D activities</p>
              </div>
              <div>
                <p className="font-semibold text-cyan-300 mb-1">Revenue share</p>
                <p>Net licensing revenue is defined as royalties and settlements, net of enforcement costs, and is then allocated between GH and the partner.</p>
                <p className="text-cyan-200 mt-1"><strong>50/50 split</strong> common when both parties contribute significantly</p>
                <p className="text-gray-400 text-xs mt-1">ARG receives larger % with significant capital investment or guaranteed enforcement costs</p>
              </div>
              <div>
                <p className="font-semibold text-cyan-300 mb-1">Term</p>
                <p>Licence runs for remaining patent life <strong>(through 2032</strong> for core MSML patent) with options to extend to continuation patents</p>
              </div>
            </div>
          </motion.div>
        )}

        {/* Royalty Metrics and Rates */}
        {(!isPitchMode || visibleBullets >= 3) && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-slate-900/80 border border-cyan-400/30 rounded-lg p-6"
          >
            <h3 className="text-cyan-400 font-semibold mb-4 text-lg">Royalty Metrics & Rates</h3>
            <div className="space-y-3 text-gray-300 text-sm">
              <div>
                <p className="font-semibold text-cyan-300 mb-1">Model-compression targets</p>
                <p><strong>0.1%–0.2%</strong> of AI-API revenues per token</p>
                <p className="text-gray-400 text-xs">Inference: $0.06–$2 per M tokens → levy: $0.00006–$0.002 per M tokens</p>
              </div>
              <div>
                <p className="font-semibold text-cyan-300 mb-1">Enterprise software (RAG/vector stores)</p>
                <p><strong>1–2%</strong> of subscription revenue</p>
                <p className="text-gray-400 text-xs">Consistent with typical patent-licensing practices</p>
              </div>
              <div>
                <p className="font-semibold text-cyan-300 mb-1">Hardware vendors</p>
                <p><strong>$0.50</strong> per AI accelerator | <strong>$1–$2</strong> per camera/phone</p>
                <p className="text-gray-400 text-xs">Content-credentialing and edge compression adoption</p>
              </div>
              <p className="text-cyan-200 text-xs pt-2 border-t border-gray-700">Industry norm: Patent licences typically 0.1%–25% of revenue</p>
            </div>
          </motion.div>
        )}
      </div>

      {/* Monetary Expectations */}
      {(!isPitchMode || visibleBullets >= 4) && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="bg-gradient-to-r from-cyan-900/40 to-blue-900/40 border border-cyan-400/40 rounded-lg p-6"
        >
          <h3 className="text-cyan-300 font-semibold mb-3 text-xl">Monetary Expectations</h3>
          <div className="grid md:grid-cols-2 gap-6 text-gray-300 text-sm">
            <div>
              <p className="mb-2">Licensing ~10 major targets with modest rates:</p>
              <p className="text-white text-lg font-bold">$30–50 M <span className="text-gray-400 font-normal text-sm">per year</span></p>
              <p className="text-cyan-200 mt-1">5-year term: <strong>$150–250 M</strong> total revenue</p>
            </div>
            <div>
              <p className="mb-2">Under 50/50 split, each party expects:</p>
              <p className="text-white text-lg font-bold">$15–25 M <span className="text-gray-400 font-normal text-sm">annually</span></p>
              <p className="text-cyan-200 mt-1">5-year total: <strong>$75–125 M</strong> per party</p>
              <p className="text-gray-400 text-xs mt-2">Upside if more licensees added or rates increased</p>
            </div>
          </div>
        </motion.div>
      )}

      {(!isPitchMode || visibleBullets >= 5) && (
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="text-center text-xl text-cyan-400 mt-6"
        >
          Next step: Confirm target list and initiate claim-chart phase.
        </motion.p>
      )}
    </div>
  </div>
);

// Main Presentation Component
const EnforcementPresentation = ({ onExit }) => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isPitchMode, setIsPitchMode] = useState(false);
  const [visibleBullets, setVisibleBullets] = useState(0);
  const [showPresenterOverlay, setShowPresenterOverlay] = useState(false);
  const [showPresenterNotes, setShowPresenterNotes] = useState(false);
  const [elapsedTime, setElapsedTime] = useState(0);
  const [isTimerRunning, setIsTimerRunning] = useState(false);
  const containerRef = useRef(null);

  const slides = [Slide1, Slide2, Slide3, Slide4, Slide5, Slide6];
  const slideTitles = [
    'Opportunity Framing',
    'Why Now (AI Economics)',
    'The IP Core',
    'Repeatable Licensing Wedges',
    'Execution Mechanics',
    'Economics & Structure'
  ];

  const totalDuration = 1200; // 20 minutes in seconds

  // Timer effect
  useEffect(() => {
    let interval;
    if (isTimerRunning && elapsedTime < totalDuration) {
      interval = setInterval(() => {
        setElapsedTime(prev => prev + 1);
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [isTimerRunning, elapsedTime]);

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Toggle presenter notes with 'N' key
      if (e.key === 'n' || e.key === 'N') {
        e.preventDefault();
        setShowPresenterNotes(prev => !prev);
        return;
      }
      
      if (isPitchMode) {
        if (e.code === 'Space') {
          e.preventDefault();
          setVisibleBullets(prev => prev + 1);
          // Adjust max bullets based on current slide
          const maxBullets = currentSlide === 5 ? 5 : 4; // Slide 6 has 5 sections
          if (visibleBullets >= maxBullets) {
            if (currentSlide < slides.length - 1) {
              setCurrentSlide(prev => prev + 1);
              setVisibleBullets(0);
            }
          }
        }
      } else {
        if (e.key === 'ArrowRight' || e.key === 'PageDown') {
          e.preventDefault();
          if (currentSlide < slides.length - 1) {
            setCurrentSlide(prev => prev + 1);
          }
        } else if (e.key === 'ArrowLeft' || e.key === 'PageUp') {
          e.preventDefault();
          if (currentSlide > 0) {
            setCurrentSlide(prev => prev - 1);
          }
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [currentSlide, isPitchMode, visibleBullets]);

  // Scroll snap
  useEffect(() => {
    if (containerRef.current) {
      const slideHeight = window.innerHeight;
      containerRef.current.scrollTo({
        top: currentSlide * slideHeight,
        behavior: 'smooth'
      });
    }
  }, [currentSlide]);

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const handlePrint = () => {
    window.print();
  };

  const CurrentSlideComponent = slides[currentSlide];

  const presentationContent = (
    <div className="fixed inset-0 w-full h-screen bg-slate-950 overflow-hidden" style={{ zIndex: 99999 }}>
      {/* Header Badge */}
      <div className="fixed top-4 left-1/2 transform -translate-x-1/2" style={{ zIndex: 100001 }}>
        <span className="px-4 py-2 bg-slate-900/90 border border-cyan-400/40 rounded-full text-cyan-300 text-sm backdrop-blur-sm">
          Restricted – Licensing Review
        </span>
      </div>

      {/* Control Panel */}
      <div className="fixed top-4 right-4 flex gap-2" style={{ zIndex: 100001 }}>
        <button
          onClick={() => setShowPresenterNotes(!showPresenterNotes)}
          className={`px-4 py-2 rounded flex items-center ${
            showPresenterNotes
              ? 'bg-cyan-500 text-white'
              : 'bg-slate-800 text-gray-300 border border-cyan-400/30'
          } hover:bg-cyan-600 transition-colors`}
          title="Toggle Presenter Notes (N)"
          data-testid="presenter-notes-btn"
        >
          <FileText size={18} />
          <span className="ml-2">Notes</span>
        </button>

        <button
          onClick={() => {
            setIsPitchMode(!isPitchMode);
            if (!isPitchMode) {
              setVisibleBullets(0);
              setIsTimerRunning(true);
            }
          }}
          className={`px-4 py-2 rounded flex items-center ${
            isPitchMode
              ? 'bg-cyan-500 text-white'
              : 'bg-slate-800 text-gray-300 border border-cyan-400/30'
          } hover:bg-cyan-600 transition-colors`}
          data-testid="live-pitch-mode-btn"
        >
          {isPitchMode ? <Pause size={18} /> : <Play size={18} />}
          <span className="ml-2">Live Pitch Mode</span>
        </button>

        {isPitchMode && (
          <button
            onClick={() => setShowPresenterOverlay(!showPresenterOverlay)}
            className="px-4 py-2 rounded bg-slate-800 text-gray-300 border border-cyan-400/30 hover:bg-slate-700 transition-colors"
            data-testid="presenter-view-btn"
          >
            Presenter View
          </button>
        )}

        <button
          onClick={handlePrint}
          className="px-4 py-2 rounded bg-slate-800 text-gray-300 border border-cyan-400/30 hover:bg-slate-700 transition-colors"
        >
          <Printer size={18} />
        </button>

        <button
          onClick={onExit}
          className="px-4 py-2 rounded bg-slate-800 text-gray-300 border border-cyan-400/30 hover:bg-slate-700 transition-colors"
        >
          <X size={18} />
          <span className="ml-2">Exit</span>
        </button>
      </div>

      {/* Presenter Overlay */}
      {isPitchMode && showPresenterOverlay && (
        <div className="fixed bottom-20 left-4 bg-slate-900/95 border border-cyan-400/40 rounded-lg p-4 backdrop-blur-sm max-w-sm" style={{ zIndex: 100001 }}>
          <div className="space-y-2 text-sm">
            <div>
              <span className="text-gray-400">Current:</span>
              <span className="text-white ml-2">{slideTitles[currentSlide]}</span>
            </div>
            {currentSlide < slides.length - 1 && (
              <div>
                <span className="text-gray-400">Next:</span>
                <span className="text-cyan-400 ml-2">{slideTitles[currentSlide + 1]}</span>
              </div>
            )}
            <div className="flex justify-between pt-2 border-t border-gray-700">
              <div>
                <span className="text-gray-400">Elapsed:</span>
                <span className="text-white ml-2">{formatTime(elapsedTime)}</span>
              </div>
              <div>
                <span className="text-gray-400">Remaining:</span>
                <span className="text-white ml-2">{formatTime(totalDuration - elapsedTime)}</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Presenter Notes Modal */}
      <PresenterNotesModal 
        isOpen={showPresenterNotes} 
        onClose={() => setShowPresenterNotes(false)} 
        slideIndex={currentSlide}
      />

      {/* Navigation Arrows */}
      {!isPitchMode && (
        <>
          {currentSlide > 0 && (
            <button
              onClick={() => setCurrentSlide(prev => prev - 1)}
              className="fixed left-4 top-1/2 transform -translate-y-1/2 p-3 bg-slate-900/80 border border-cyan-400/30 rounded-full hover:bg-cyan-500 transition-colors"
              style={{ zIndex: 100001 }}
              data-testid="prev-slide-btn"
            >
              <ChevronLeft size={24} className="text-white" />
            </button>
          )}

          {currentSlide < slides.length - 1 && (
            <button
              onClick={() => setCurrentSlide(prev => prev + 1)}
              className="fixed right-4 top-1/2 transform -translate-y-1/2 p-3 bg-slate-900/80 border border-cyan-400/30 rounded-full hover:bg-cyan-500 transition-colors"
              style={{ zIndex: 100001 }}
              data-testid="next-slide-btn"
            >
              <ChevronRight size={24} className="text-white" />
            </button>
          )}
        </>
      )}

      {/* Slides Container */}
      <div
        ref={containerRef}
        className="h-screen overflow-y-auto snap-y snap-mandatory scroll-smooth relative z-10"
        style={{ scrollSnapType: 'y mandatory' }}
      >
        {slides.map((SlideComponent, index) => (
          <div key={index} className="snap-start">
            <SlideComponent
              isPitchMode={isPitchMode && index === currentSlide}
              visibleBullets={visibleBullets}
            />
          </div>
        ))}
      </div>

      {/* Progress Indicator */}
      <div className="fixed bottom-4 left-1/2 transform -translate-x-1/2 flex gap-2" style={{ zIndex: 100001 }}>
        {slides.map((_, index) => (
          <button
            key={index}
            onClick={() => !isPitchMode && setCurrentSlide(index)}
            className={`w-2 h-2 rounded-full transition-all ${
              index === currentSlide
                ? 'bg-cyan-400 w-8'
                : 'bg-gray-600 hover:bg-gray-500'
            }`}
          />
        ))}
      </div>

      {/* Back to Assets Link */}
      <button
        onClick={onExit}
        className="fixed bottom-4 left-4 text-gray-400 hover:text-cyan-400 transition-colors flex items-center gap-2 text-sm"
        style={{ zIndex: 100001 }}
        data-testid="back-to-assets-btn"
      >
        <ArrowLeft size={16} />
        Back to Assets
      </button>
    </div>
  );

  // Use createPortal to render outside the normal DOM hierarchy
  return createPortal(presentationContent, document.body);
};

// Main Export Component for Integration
export default function EnforcementLicensingModule({ isOpen, onClose }) {
  const [showModal, setShowModal] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  // Sync external isOpen prop with internal state
  React.useEffect(() => {
    if (isOpen && !isAuthenticated) {
      setShowModal(true);
    }
  }, [isOpen, isAuthenticated]);

  const handleSuccess = () => {
    setIsAuthenticated(true);
    setShowModal(false);
    // Update URL without page reload
    window.history.pushState({}, '', '/assets/enforcement');
  };

  const handleExit = () => {
    setIsAuthenticated(false);
    setShowModal(false);
    // Return to assets page
    window.history.pushState({}, '', '/assets');
    if (onClose) onClose();
  };

  const handleModalClose = () => {
    setShowModal(false);
    if (onClose) onClose();
  };

  return (
    <>
      {/* Password Gate Modal */}
      <PasswordGateModal
        isOpen={showModal}
        onClose={handleModalClose}
        onSuccess={handleSuccess}
      />

      {/* Gated Presentation */}
      {isAuthenticated && (
        <EnforcementPresentation onExit={handleExit} />
      )}
    </>
  );
}
